/**
 * Sample Parts Library — pre-built example scripts with inline comments.
 * Each sample teaches a specific workflow pattern.
 */

export interface SamplePart {
  id: string;
  name: string;
  description: string;
  category: "beginner" | "intermediate" | "advanced";
  script: string;
}

export const SAMPLE_PARTS: SamplePart[] = [
  {
    id: "sample-filleted-box",
    name: "Filleted Box",
    description: "A simple box with rounded edges — learn primitives + modifiers",
    category: "beginner",
    script: `// Filleted Box — Your first part!
// This script creates a box and rounds all its edges.

const { makeBaseBox } = replicad;

function main() {
  // Step 1: Create a 30×20×15mm box
  const box = makeBaseBox(30, 20, 15);

  // Step 2: Apply a 2mm fillet (rounding) to all edges
  const filleted = box.fillet(2);

  return filleted;
}`,
  },
  {
    id: "sample-bracket",
    name: "L-Bracket",
    description: "Extruded L-shape with mounting holes — booleans + positioning",
    category: "beginner",
    script: `// L-Bracket — Boolean operations
// Creates an L-shaped bracket with two mounting holes.

const { makeBaseBox, makeCylinder } = replicad;

function main() {
  // Step 1: Create the vertical plate (40×30×5mm)
  const vertical = makeBaseBox(40, 5, 30);

  // Step 2: Create the horizontal base (40×25×5mm)
  const horizontal = makeBaseBox(40, 25, 5);

  // Step 3: Fuse them into an L-shape
  const bracket = vertical.fuse(horizontal);

  // Step 4: Cut mounting holes (8mm diameter, through the base)
  const hole1 = makeCylinder(4, 10).translate(10, 12, -2);
  const hole2 = makeCylinder(4, 10).translate(30, 12, -2);
  const withHoles = bracket.cut(hole1).cut(hole2);

  // Step 5: Fillet the inside corner for strength
  return withHoles.fillet(1);
}`,
  },
  {
    id: "sample-bearing-housing",
    name: "Bearing Housing",
    description: "Revolved shell with bolt pattern — revolve + shell + pattern",
    category: "intermediate",
    script: `// Bearing Housing — Revolve + Shell
// A cylindrical housing made by revolving a profile.

const { Sketcher, makeCylinder } = replicad;

function main() {
  // Step 1: Draw the cross-section profile
  const profile = new Sketcher("XZ")
    .movePointerTo([15, 0])
    .lineTo([25, 0])
    .lineTo([25, 20])
    .lineTo([20, 20])
    .lineTo([20, 5])
    .lineTo([15, 5])
    .close();

  // Step 2: Revolve 360° around the Z axis to make the housing
  const housing = profile.revolve([0, 0, 1]);

  // Step 3: Cut the center bore (15mm radius)
  const bore = makeCylinder(15, 25).translateZ(-2);
  const bored = housing.cut(bore);

  // Step 4: Add a fillet on the outer top edge
  return bored.fillet(1.5);
}`,
  },
  {
    id: "sample-pipe-fitting",
    name: "Pipe Fitting",
    description: "Hollow tube with flanges — cylinders + shell + fuse",
    category: "intermediate",
    script: `// Pipe Fitting — Flanges + Shell
// A pipe section with flanged ends for bolted connections.

const { makeCylinder } = replicad;

function main() {
  // Step 1: Main pipe body (outer diameter 30mm, length 50mm)
  const outerPipe = makeCylinder(15, 50);
  const innerBore = makeCylinder(12, 55).translateZ(-2.5);
  const pipe = outerPipe.cut(innerBore);

  // Step 2: Bottom flange (wider disc)
  const bottomFlange = makeCylinder(25, 4);
  const bottomHole = makeCylinder(12, 6).translateZ(-1);
  const bottomDisc = bottomFlange.cut(bottomHole);

  // Step 3: Top flange
  const topFlange = makeCylinder(25, 4).translateZ(46);
  const topHole = makeCylinder(12, 6).translateZ(45);
  const topDisc = topFlange.cut(topHole);

  // Step 4: Combine all parts
  return pipe.fuse(bottomDisc).fuse(topDisc).fillet(0.5);
}`,
  },
  {
    id: "sample-enclosure",
    name: "Electronics Enclosure",
    description: "Shelled box with ventilation slots — shell + pattern cuts",
    category: "advanced",
    script: `// Electronics Enclosure — Shell + Pattern
// A hollowed box with ventilation slots on the side.

const { makeBaseBox } = replicad;

function main() {
  // Step 1: Create outer box (80×50×30mm)
  const outer = makeBaseBox(80, 50, 30);

  // Step 2: Shell it — 2mm walls, open the top face (at z=30)
  const shelled = outer.shell(-2, (f) => f.inPlane("XY", 30));

  // Step 3: Add ventilation slots through the front face
  let result = shelled;
  for (let i = 0; i < 5; i++) {
    const slot = makeBaseBox(10, 3, 12).translate(8 + i * 13, -1, 9);
    result = result.cut(slot);
  }

  return result;
}`,
  },
];
